import { useState, useEffect } from "react";
import axios from "axios";
import { useAuth } from "@/contexts/AuthContext";
import {
  FileText, Search, RefreshCw, Loader2, Server, AlertTriangle,
  XCircle, Info, ChevronDown, ChevronUp, Clock, Filter, Monitor
} from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const LEVEL_STYLES = {
  Critical: { bg: "bg-purple-500/10", border: "border-purple-500/20", text: "text-purple-400", icon: XCircle },
  Error: { bg: "bg-red-500/10", border: "border-red-500/20", text: "text-red-400", icon: XCircle },
  Warning: { bg: "bg-yellow-500/10", border: "border-yellow-500/20", text: "text-yellow-400", icon: AlertTriangle },
  Information: { bg: "bg-blue-500/10", border: "border-blue-500/20", text: "text-blue-400", icon: Info },
  Verbose: { bg: "bg-zinc-500/10", border: "border-zinc-500/20", text: "text-zinc-400", icon: Info },
};

function EventRow({ event, onClick }) {
  const style = LEVEL_STYLES[event.Level] || LEVEL_STYLES.Information;
  const LevelIcon = style.icon;
  return (
    <button onClick={() => onClick(event)}
      className={`w-full flex items-start gap-3 px-3 py-2.5 border-b border-white/5 hover:bg-white/[0.02] text-left transition-all`}
      data-testid={`event-row-${event.EventId}`}>
      <LevelIcon className={`w-4 h-4 mt-0.5 shrink-0 ${style.text}`} />
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <Badge className={`text-[9px] border ${style.bg} ${style.text} ${style.border} px-1.5 py-0`}>
            {event.Level}
          </Badge>
          <span className="text-xs font-mono text-zinc-500">{event.Source}</span>
          <span className="text-[10px] text-zinc-600 ml-auto shrink-0">{event.Time}</span>
        </div>
        <p className="text-xs text-zinc-300 mt-1 line-clamp-2 leading-relaxed">{event.Message}</p>
      </div>
    </button>
  );
}

function SummaryCard({ summary }) {
  return (
    <div className="grid grid-cols-3 gap-3">
      {summary.map(s => (
        <div key={s.LogName} className="p-3 bg-[#0c0c0e] border border-white/5 rounded-lg"
          data-testid={`summary-${s.LogName}`}>
          <p className="text-xs text-zinc-400 font-medium mb-2">{s.LogName}</p>
          <div className="flex gap-2 flex-wrap">
            {s.Critical > 0 && (
              <Badge className="bg-purple-500/10 text-purple-400 border-purple-500/20 text-[10px]">
                {s.Critical} Critical
              </Badge>
            )}
            {s.Error > 0 && (
              <Badge className="bg-red-500/10 text-red-400 border-red-500/20 text-[10px]">
                {s.Error} Error
              </Badge>
            )}
            {s.Warning > 0 && (
              <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20 text-[10px]">
                {s.Warning} Warning
              </Badge>
            )}
            <Badge className="bg-blue-500/10 text-blue-400 border-blue-500/20 text-[10px]">
              {s.Information || 0} Info
            </Badge>
            <span className="text-[10px] text-zinc-600 ml-auto">{s.Total} total</span>
          </div>
        </div>
      ))}
    </div>
  );
}

export default function EventLogViewer() {
  const { user } = useAuth();
  const [servers, setServers] = useState([]);
  const [selectedServer, setSelectedServer] = useState("");
  const [logName, setLogName] = useState("System");
  const [level, setLevel] = useState("");
  const [hoursBack, setHoursBack] = useState(24);
  const [maxEvents, setMaxEvents] = useState(50);
  const [events, setEvents] = useState([]);
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(false);
  const [summaryLoading, setSummaryLoading] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState(null);
  const [searchFilter, setSearchFilter] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/servers`);
        const winServers = res.data.filter(s => s.os_type === "windows" && s.winrm_configured);
        setServers(winServers);
        if (winServers.length > 0) setSelectedServer(winServers[0].id);
      } catch { toast.error("Failed to load servers"); }
    })();
  }, []);

  const fetchEvents = async () => {
    if (!selectedServer) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/event-logs/query`, {
        server_id: selectedServer, log_name: logName,
        level: level || null, max_events: maxEvents, hours_back: hoursBack
      });
      if (res.data.success) {
        setEvents(res.data.events || []);
        if (res.data.events?.length === 0) toast.info("No events found for the selected filters");
      } else {
        toast.error(res.data.error || "Failed to fetch events");
      }
    } catch { toast.error("Failed to fetch events"); }
    finally { setLoading(false); }
  };

  const fetchSummary = async () => {
    if (!selectedServer) return;
    setSummaryLoading(true);
    try {
      const res = await axios.get(`${API}/event-logs/summary?server_id=${selectedServer}&hours_back=${hoursBack}`);
      if (res.data.success) setSummary(res.data.summary || []);
      else toast.error(res.data.error || "Failed");
    } catch { toast.error("Failed to load summary"); }
    finally { setSummaryLoading(false); }
  };

  useEffect(() => {
    if (selectedServer) { fetchSummary(); fetchEvents(); }
  }, [selectedServer]);

  const filteredEvents = searchFilter
    ? events.filter(e =>
        (e.Message || "").toLowerCase().includes(searchFilter.toLowerCase()) ||
        (e.Source || "").toLowerCase().includes(searchFilter.toLowerCase())
      )
    : events;

  return (
    <div className="p-6 space-y-5" data-testid="event-log-viewer-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold font-mono text-white tracking-tight flex items-center gap-3">
            <Monitor className="w-8 h-8 text-amber-400" /> Event Log Viewer
          </h1>
          <p className="text-zinc-400 text-sm mt-1">
            Windows Event Logs — System, Application, Security
          </p>
        </div>
      </div>

      {/* Server selector */}
      <div className="flex items-center gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Server className="w-4 h-4 text-zinc-500" />
          <Label className="text-xs text-zinc-400">Server</Label>
        </div>
        <Select value={selectedServer} onValueChange={setSelectedServer}>
          <SelectTrigger className="w-72 bg-black/50 border-white/10 text-white text-sm h-9"
            data-testid="eventlog-server-select">
            <SelectValue placeholder="Select Windows server..." />
          </SelectTrigger>
          <SelectContent className="bg-[#0c0c0e] border-white/10">
            {servers.length === 0 ? (
              <SelectItem value="none" disabled>No Windows servers with WinRM</SelectItem>
            ) : servers.map(s => (
              <SelectItem key={s.id} value={s.id}>
                <span className="text-sm">{s.name} ({s.ip_address})</span>
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {servers.length === 0 ? (
        <Card className="bg-[#0c0c0e] border-white/5">
          <CardContent className="py-16 text-center space-y-3">
            <Monitor className="w-14 h-14 text-zinc-700 mx-auto" />
            <p className="text-lg font-medium text-white">No Windows Servers</p>
            <p className="text-zinc-500 text-sm max-w-md mx-auto">
              Add a Windows server with WinRM credentials to view event logs.
            </p>
          </CardContent>
        </Card>
      ) : (
        <>
          {/* Summary */}
          {summaryLoading ? (
            <div className="flex items-center justify-center py-6">
              <Loader2 className="w-5 h-5 animate-spin text-amber-400" />
            </div>
          ) : summary.length > 0 && (
            <SummaryCard summary={summary} />
          )}

          {/* Filters */}
          <div className="flex items-end gap-3 flex-wrap p-3 bg-[#0c0c0e] border border-white/5 rounded-lg">
            <div className="space-y-1">
              <Label className="text-[10px] text-zinc-500">Log</Label>
              <Select value={logName} onValueChange={setLogName}>
                <SelectTrigger className="w-36 bg-black/50 border-white/10 text-white text-xs h-8"
                  data-testid="log-name-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0c0c0e] border-white/10">
                  {["System", "Application", "Security"].map(l => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-zinc-500">Level</Label>
              <Select value={level || "all"} onValueChange={v => setLevel(v === "all" ? "" : v)}>
                <SelectTrigger className="w-32 bg-black/50 border-white/10 text-white text-xs h-8"
                  data-testid="level-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0c0c0e] border-white/10">
                  <SelectItem value="all">All Levels</SelectItem>
                  {["Critical", "Error", "Warning", "Information"].map(l => (
                    <SelectItem key={l} value={l}>{l}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-zinc-500">Time Range</Label>
              <Select value={String(hoursBack)} onValueChange={v => setHoursBack(parseInt(v))}>
                <SelectTrigger className="w-28 bg-black/50 border-white/10 text-white text-xs h-8"
                  data-testid="hours-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0c0c0e] border-white/10">
                  {[1, 6, 12, 24, 48, 72, 168].map(h => (
                    <SelectItem key={h} value={String(h)}>{h < 24 ? `${h}h` : `${h/24}d`}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[10px] text-zinc-500">Max Events</Label>
              <Select value={String(maxEvents)} onValueChange={v => setMaxEvents(parseInt(v))}>
                <SelectTrigger className="w-20 bg-black/50 border-white/10 text-white text-xs h-8">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent className="bg-[#0c0c0e] border-white/10">
                  {[25, 50, 100, 200].map(n => (
                    <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <Button size="sm" onClick={fetchEvents} disabled={loading}
              className="bg-amber-600 hover:bg-amber-700 text-white h-8"
              data-testid="fetch-events-btn">
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin mr-1" /> : <Search className="w-3.5 h-3.5 mr-1" />}
              Query
            </Button>
            <Button size="sm" variant="outline" onClick={() => { fetchEvents(); fetchSummary(); }}
              disabled={loading}
              className="bg-white/5 border-white/10 hover:bg-white/10 text-white h-8"
              data-testid="refresh-events-btn">
              <RefreshCw className="w-3.5 h-3.5" />
            </Button>
            <div className="flex-1 min-w-[200px]">
              <Input value={searchFilter} onChange={e => setSearchFilter(e.target.value)}
                placeholder="Filter results..."
                className="bg-black/50 border-white/10 text-white text-xs h-8 font-mono"
                data-testid="event-search-input" />
            </div>
          </div>

          {/* Events List */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="w-6 h-6 animate-spin text-amber-400" />
            </div>
          ) : filteredEvents.length > 0 ? (
            <div className="bg-[#0c0c0e] border border-white/5 rounded-lg overflow-hidden">
              <div className="flex items-center justify-between px-3 py-2 border-b border-white/5">
                <p className="text-xs text-zinc-400">
                  {filteredEvents.length} event{filteredEvents.length !== 1 && "s"}
                  {searchFilter && ` (filtered from ${events.length})`}
                </p>
                <p className="text-[10px] text-zinc-600">{logName} Log</p>
              </div>
              <div className="max-h-[500px] overflow-y-auto">
                {filteredEvents.map((event, i) => (
                  <EventRow key={`${event.EventId}-${i}`} event={event} onClick={setSelectedEvent} />
                ))}
              </div>
            </div>
          ) : events.length === 0 && !loading ? (
            <Card className="bg-[#0c0c0e] border-white/5">
              <CardContent className="py-12 text-center space-y-2">
                <FileText className="w-10 h-10 text-zinc-700 mx-auto" />
                <p className="text-sm text-white">No events found</p>
                <p className="text-xs text-zinc-500">Try adjusting your filters or time range, then click Query.</p>
              </CardContent>
            </Card>
          ) : null}
        </>
      )}

      {/* Event Detail Dialog */}
      <Dialog open={!!selectedEvent} onOpenChange={() => setSelectedEvent(null)}>
        <DialogContent className="bg-[#0c0c0e] border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          {selectedEvent && (() => {
            const style = LEVEL_STYLES[selectedEvent.Level] || LEVEL_STYLES.Information;
            return (
              <>
                <DialogHeader>
                  <DialogTitle className="font-mono flex items-center gap-2 text-base">
                    <FileText className="w-4 h-4 text-amber-400" />
                    Event {selectedEvent.EventId}
                  </DialogTitle>
                </DialogHeader>
                <div className="space-y-3">
                  <div className="flex items-center gap-2 flex-wrap">
                    <Badge className={`text-xs border ${style.bg} ${style.text} ${style.border}`}>
                      {selectedEvent.Level}
                    </Badge>
                    <span className="text-xs text-zinc-400 font-mono">{selectedEvent.Source}</span>
                    <span className="text-xs text-zinc-600 ml-auto">{selectedEvent.Time}</span>
                  </div>
                  <div className="p-3 bg-black/50 border border-white/5 rounded-lg">
                    <pre className="text-xs text-zinc-300 font-mono whitespace-pre-wrap leading-relaxed"
                      data-testid="event-detail-message">
                      {selectedEvent.Message}
                    </pre>
                  </div>
                </div>
              </>
            );
          })()}
        </DialogContent>
      </Dialog>
    </div>
  );
}
